import { defineStore } from 'pinia'
import { get } from '@vueuse/core'
import { useStore } from '.'
import { add, div, fix, mul, time2Date } from '#imports'
import { accounts, accountsName, getOverviewByCode, openOrders, overview, positionConfig, positions, positionsExtra, wallet } from '~/apis/socket'
import { getAccountAssetTypeList, getAccountOpend, getMarketTrades, setPositionConfig } from '@/apis/future'
import { getContractsHotSymbolsApi } from '~/apis/markets'
import { usePanelStore } from './panel'

export const useFutureStore = defineStore('future', {
  state: () => ({
    defaultCode: 'BTCUSDT',
    type: 'futures',
    code: '',
    side: 1,
    bbo: {},
    marketTrades: [],
    accountType: 1, // 1 主账户 10 体验金 11 跟单 30 赠金
    modal: {
      transfer: false,
      picker: false,
      orderConfirm: false,
      adjustLeverage: false,
      calculator: false,
      unitSetting: false,
      flashAll: false,
      reverse: false,
      cancelAll: false,
    },
    modalData: {
      adjustLeverage: {},
      orderConfirm: {},
      reverse: {},
    },
    drawer: {
      bonus: false,
      setting: false,
    },
    orderConfirming: false,
    unit: '', // lot 张，quote 名义价值， base 币种 、 cost 成本价值
    accountOpend: null, // future: true
    preferences: {}, // 偏好设置
    accountAssetTypeList: [],
    typeRaw: {},
    hotSymbols: [], // 热门榜单
  }),
  getters: {
    curInfo(s) {
      return this.getInfo(s.code)
    },
    base() {
      return this.curInfo.baseAsset ?? ''
    },
    quote() {
      return this.curInfo.quoteAsset ?? 'USDT'
    },
    baseM() {
      return this.curInfo.baseM ?? ''
    },
    as() {
      return this.curInfo.as
    },
    ps() {
      return this.curInfo.ps
    },
    scale() {
      return this.isLot ? 0 : this.isLotBase ? this.as : this.ps
    },
    id() {
      return this.curInfo.contractId
    },
    steps() {
      return this.curInfo?.steps?.replace(/\s/g, '').split(',') // 屎
    },
    isBonusOpen() {
      return this.accountOpend?.bonusCurrencies?.length > 0
    },
    isLot() {
      return this.unit === 'lot'
    },
    isLotBase() {
      return this.unit === 'base'
    },
    isLotQuote() {
      return this.unit === 'quote'
    },
    isLotCost() {
      return this.unit === 'cost'
    },
    isMain() {
      return this.accountType === 1
    },
    isBonus() {
      return this.accountType === 10
    },
    isFollow() {
      return this.accountType === 11
    },
    isGrants() {
      return this.accountType === 30
    },
    size() {
      return this.curInfo?.contractSize
    },
    overview() {
      return get(overview.futures)[this.id] || {}
    },
    accountName() {
      return accountsName[this.accountType]
    },
    favIds() {
      return useStore().favRaw.contractIdList ?? []
    },
    getInfo() {
      const { contractsRaw, currenciesRaw } = useStore()
      return code => contractsRaw[code] ?? currenciesRaw[code] ?? {}
    },
    getAidByType() {
      return (type = this.accountType) => {
        const name = accountsName[type]
        return accounts.value[name]?.aid
      }
    },
    getBalanceByCode() {
      return (code = this.baseM, name = this.accountName) => {
        const id = this.getInfo(code).id
        return wallet.value[name]?.[id] ?? {}
      }
    },
    getOverviewByCode() {
      return (code = this.code, type = this.type) => {
        const id = this.getInfo(code)?.contractId
        return getOverviewByCode(id || code, type)
      }
    },
    /**
     *
     * 计算当前合约成本
     * 合约成本(U): cost = 价格 * 面值(contractSize) * 初始保证金率(1 / 杠杆倍数)  + price * 合约面值 * taker手续费 * 2
     */
    getOneLotCost() {
      const panel = usePanelStore()
      return ({ price, code = this.code, leverage }) => {
        price ||= this.getLastPrice(code)
        leverage ??= this.getPositionConfigByCode(code)?.leverage
        const { size = 1, takerFeeRate = 0 } = this.getInfo(code)
        const olc = mul(price, size)
        const leverageFactor = div(1, leverage)
        const feeFactor = mul(takerFeeRate, 2)
        return panel.pe === 1 ? olc.mul(add(leverageFactor, feeFactor)) : olc.div(leverage)
      }
    },
    getLastPrice() {
      return code => this.getOverviewByCode(code)?.c
    },
    getMarkPrice() {
      return code => this.getOverviewByCode(code)?.mark
    },
    getRealAmount() {
      return (n, { code = this.code, price, forceQuote } = {}) => {
        if (n !== undefined) {
          if (this.isLot && !forceQuote) {
            return n
          }
          const { size } = this.getInfo(code)
          if (size) {
            const coin = mul(n, size)
            if (this.isLotQuote || this.isLotCost || forceQuote) {
              price ||= this.getLastPrice(code) || this.getMarkPrice(code)
              if (price) {
                return mul(price, coin)
              }
              return null
            }
            return coin
          }
          return null
        }
        return n
      }
    },
    positionsN() {
      return this.positions.length
    },
    openOrdersN() {
      return this.openOrders.length
    },
    positionsByCode() {
      const store = useStore()
      return get(positions).reduce((sofar, cur) => {
        const sy = store.getCodeById(cur.contractId)

        cur.code = sy
        cur.amount = cur.posiQty
        cur.price = cur.openPrice
        cur.realAmount = this.getRealAmount(cur.amount, { code: sy })
        cur.side = Math.sign(cur.posiQty)
        cur.size = cur.contractUnit
        cur.interest = mul(cur.openAmt, cur.maintainMarginRate) // 维持保证金
        cur.isolated = cur.marginType === 2
        cur.isFuture = cur.accountType === 1
        cur.isBonus = cur.accountType === 10
        cur.isFollow = cur.accountType === 11
        cur.accountName = accountsName[cur.accountType]
        sofar[sy] ??= []
        sofar[sy].push(cur)
        sofar[cur.id] = cur
        sofar._ALL.push(cur)
        sofar[cur.accountName] ??= { }
        sofar[cur.accountName][sy] ??= []
        sofar[cur.accountName][sy].push(cur)
        if (cur.marginType === 1) {
          sofar[cur.accountName]._interest = add(sofar[cur.accountName]._interest || 0, cur.interest)
        }
        return sofar
      }, { _ALL: [], _interest: 0 })
    },
    positions() {
      return this.positionsByCode._ALL
    },
    positionsByType() {
      return (type = this.accountType) => this.positions.filter(i => i.accountType === type)
    },
    openOrdersByCode() {
      const store = useStore()
      const res = { _ALL: [] }
      get(openOrders).forEach((i) => {
        const sy = store.getCodeById(i.contractId)
        i.code = sy
        i.lm = i.orderStatus !== 0
        i.tpsl = (i.stopType === 2 || i.stopType === 1) && i.orderStatus === 0
        i.trigger = i.stopType === 0 && i.stopDirection && i.orderStatus === 0
        i.conditionType = i.lm ? 0 : i.trigger ? 1 : i.tpsl ? 2 : 4
        i.isFuture = i.accountType === 1
        i.isBonus = i.accountType === 10
        i.isFollow = i.accountType === 11
        i.isolated = i.marginType === 2
        i.accountName = accountsName[i.accountType]
        res[sy] ??= []
        res[sy].push(i)
        res[i.id] = i
        res._ALL.push(i)
        res[i.accountName] ??= { }
        res[i.accountName][sy] ??= []
        res[i.accountName][sy].push(i)
      })
      return res
    },
    openOrders() {
      return this.openOrdersByCode._ALL
    },
    openOrdersByType() {
      return (type = this.accountType) => this.openOrders.filter(i => i.accountType === type)
    },
    getStops() {
      return ({ code, id, accountName = this.accountName }) => {
        const orders = this.openOrdersByCode[accountName]?.[code]

        const stops = []
        if (orders) {
          orders.forEach((i) => {
            if (i.stopType === 1 && i.pid === id && i.orderStatus === 0) { // 仓位止盈止损
              stops[i.stopCondition - 1] = i
            }
          })
        }
        return stops
      }
    },
    getLotsByCode() { // 按code取仓位张数
      return (code = this.code, name = this.accountName) => {
        const lots = { '1': 0, '-1': 0 }
        this.positionsByCode[name]?.[code]?.forEach((i) => {
          lots[i.side] += Math.abs(i.posiQty)
        })
        lots.max = Math.max(lots[1], lots['-1'], 0)
        lots.min = Math.min(lots[1], lots['-1'])
        lots.total = lots[1] - lots['-1']
        return lots
      }
    },
    getPositionLotsByCode() { // 按code取仓位张数
      return (code = this.code, name = this.accountName) => {
        const lots = { '1': 0, '-1': 0 }
        this.positionsByCode[name]?.[code]?.forEach((i) => {
          lots[i.side] += Math.abs(i.posiQty)
        })
        lots.max = Math.max(lots[1], lots['-1'], 0)
        lots.min = Math.min(lots[1], lots['-1'])
        lots.total = lots[1] - lots['-1']
        return lots
      }
    },
    getOrderLotsByCode() { // 按code取委托张数
      return (code = this.code, name = this.accountName) => {
        const lots = { '1': 0, '-1': 0 }
        this.openOrdersByCode[name]?.[code]?.forEach((i) => {
          if (i.positionEffect === 1 && i.orderSubType === 0)
            lots[i.side] += Math.abs(i.leftQuantity)
        })
        lots.max = Math.max(lots[1], lots['-1'], 0)
        lots.min = Math.min(lots[1], lots['-1'])
        lots.total = lots[1] - lots['-1']
        return lots
      }
    },
    getTotalLotsByCode() { // 按code取仓位+委托张数
      return (code = this.code, name = this.accountName) => {
        const lots = { '1': 0, '-1': 0 }
        this.positionsByCode[name]?.[code]?.forEach((i) => {
          lots[i.side] += Math.abs(i.posiQty)
        })
        this.openOrdersByCode[name]?.[code]?.forEach((i) => {
          if (i.positionEffect === 1 && i.orderSubType === 0)
            lots[i.side] += Math.abs(i.quantity)
        })
        lots.max = Math.max(lots[1], lots['-1'], 0)
        return lots
      }
    },
    getMarginBalanceByCode() {
      return (code = this.code, name = this.accountName) => {
        const { trade = 0, real = 0, upnl = 0 } = this.getBalanceByCode(code, name) ?? {}
        const { isolated } = this.positionConfig
        return isolated ? trade : Math.max(add(real, Math.max(upnl, 0)), 0)
      }
    },
    getMarginBalanceFrozenByCode() {
      return (code) => {
        return this.getBalanceByCode(code)?.frozen ?? 0
      }
    },
    // 账户权益
    getEquity() {
      return (accountName = this.accountName) => {
        const w = this.getBalanceByCode(this.baseM, accountName)
        const t = add(w.real, w.initMargin).add(w.frozenForTrade || 0)
        const isolated = this.isolatedPositionsOverview()
        // console.log('@isolated', +isolated.frozen, +isolated.unp, +isolated.margin)
        // 全仓权益 = 账户权益-逐仓(委托单)冻结-逐仓未实现盈亏（需要大于0  小于 0 不减）-逐仓仓位保证金
        const crossEquity = sub(t, isolated.frozen).sub(isolated.unp).sub(isolated.margin)

        return {
          t,
          cross: crossEquity,
        }
      }
    },
    isolatedPositionsOverview() {
      return (type = this.accountType) => {
        const isolatedPosis = this.positionsByType(type).filter(i => i.isolated)
        const isolated = isolatedPosis.reduce((sofar, cur) => {
          sofar.unpl = add(sofar.unpl, cur.unRealizedProfit)
          sofar.unp = add(sofar.unp, Math.max(cur.unRealizedProfit, 0))
          sofar.margin = add(sofar.margin, cur.initMargin)
          return sofar
        }, {
          frozen: 0,
          unpl: 0,
          unp: 0,
          margin: 0,
        })

        // 逐仓 && 限价单 && 开仓
        const openOrders = this.openOrdersByType(type).filter(i => i.lm && i.isolated && i.positionEffect === 1)
        // 逐仓冻结
        const frozen = openOrders.reduce((sofar, cur) => {
          const { contractSize = 0 } = this.getInfo(cur.code)
          sofar = add(sofar, mul(Math.abs(cur.leftQuantity), contractSize).mul(cur.price).mul(cur.initMarginRate))
          return sofar
        }, 0)

        isolated.frozen = frozen

        return isolated
      }
    },
    equity() {
      return (accountName = this.accountName) => {
        const w = this.getBalanceByCode(this.baseM, accountName)
        const t = add(w.real, w.initMargin).add(w.frozenForTrade || 0)
      }
    },
    riskLimits() {
      const limits = this.curInfo?.riskLimits ?? []
      const feeRate = mul(this.maxFeeRate, 2)
      return limits.map((i) => {
        i.ratio = limits.reduce((sofar, cur) => {
          const { leverage } = cur
          sofar[leverage] = leverage === i.leverage ? 1 : div(div(1, i.leverage).add(feeRate), div(1, leverage).add(feeRate))
          return sofar
        }, {})
        return i
      })
    },
    getRiskLimitByLeverage() {
      const riskLimits = this.riskLimits
      const defaultLeverage = this.positionConfig.leverage
      return (leverage = defaultLeverage) => {
        for (let i = riskLimits.length - 1; i >= 0; i--) {
          const risk = riskLimits[i]
          if (risk.leverage >= leverage)
            return risk
        }
        return riskLimits[1]
      }
    },
    getRiskLimitByLots() {
      const riskLimits = this.riskLimits
      return (lots) => {
        for (let i = 0; i < riskLimits.length; i++) {
          const risk = riskLimits[i]
          if (risk.maxSize >= +lots)
            return risk
        }
        return riskLimits[2]
      }
    },
    orderMaxSize() {
      return this.curInfo?.orderMaxSize ?? Number.POSITIVE_INFINITY
    },
    getPositionConfigByCode() {
      return (code, data, accountName = this.accountName) => {
        const { contractId, riskLimits = [] } = this.getInfo(code) ?? {}
        const { loading, [contractId]: o } = get(positionConfig)[accountName] ?? {}
        const { leverage = 10, isolated = false, auto = false } = data ?? o ?? {}
        const { min } = this.getLotsByCode(code)
        const { leverage: autoMaxLeverage } = riskLimits.at(-1) ?? {}
        const { maxSize } = riskLimits.reduceRight((sofar, cur) => {
          if (!sofar) {
            if (auto) {
              if (cur.maxSize >= min)
                sofar = cur
            }
            else {
              if (leverage <= cur.leverage)
                sofar = cur
            }
          }
          return sofar
        }, null) ?? riskLimits.at(-1) ?? {}

        const { leverage: maxLeverage } = riskLimits[0] ?? {}
        return {
          loading,
          code,
          auto,
          isolated,
          marginType: isolated ? 2 : 1,
          leverage,
          maxLeverage,
          maxSize,
          autoMaxLeverage,
          ...o,
        }
      }
    },
    positionConfig() {
      return this.getPositionConfigByCode(this.code)
    },
    positionsExtra() {
      return get(positionsExtra)
    },
    getUpnlById() {
      return (id) => {
        const o = this.positionsByCode[id]
        if (o) {
          return this.positionsExtra[id]?.upnl
        }
        else {
          return this.positionsByType().reduce((acc, next) => {
            const { upnl = 0 } = this.positionsExtra[next.id] ?? {}
            return add(acc, upnl)
          }, 0)
        }
      }
    },
    crossMarginRiskRateByType() {
      return (o) => {
        const { _interest = 0 } = this.positionsByCode[o.accountName] ?? {}
        const { totalBalance, upnl } = this.getBalanceByCode(this.getInfo(o.code).baseM, o.accountName)
        if (totalBalance && upnl)
          return div(_interest, add(totalBalance, upnl)).mul(100).toFixed(2)
      }
    },
    getRiskRateById() {
      return (id) => {
        const o = this.positionsByCode[id]
        if (o) {
          if (o.isolated) {
            const e = this.positionsExtra[id]
            if (e)
              return div(o.interest, add(o.initMargin, e.upnl)).mul(100).toFixed(2)
          }
          else {
            return this.crossMarginRiskRateByType(o)
          }
        }
      }
    },
    // 是否双向持仓
    isHedgeMode() {
      const { privateData } = useStore()
      if (this.accountType === 10) {
        return privateData.bonusPreference?.future.dualSide
      }
      return privateData.preference?.future.dualSide
    },
  },
  actions: {
    showModal(key) {
      this.modal[key] = true
    },
    hideModal(key) {
      this.modal[key] = false
    },
    updateUnit(v) {
      this.unit = v
      localStorage.setItem('ttx-unit', v)
    },
    async getAccountAssetTypeList() {
      const { success, data } = await getAccountAssetTypeList()
      if (success) {
        this.accountAssetTypeList = data.map(i => ({
          ...i,
          label: i.desc,
          key: i.code,
        }))
        this.typeRaw = data.reduce((acc, next) => {
          acc[next.code] = next.desc
          return acc
        })
      }
    },
    async setPositionConfig({ contractId = this.id, isolated = this.positionConfig.isolated, leverage = this.positionConfig.leverage, accountName = this.accountName }, opts) {
      return setPositionConfig({ contractId, isolated, leverage }, accountName === 'bonus', opts)
    },
    async getMarketTrades(contractId = this.id) {
      const { success, data } = await getMarketTrades({ contractId })
      if (success) {
        this.marketTrades = data.trades.map((t) => {
          const [time, price, amount, side] = t
          return {
            id: time,
            price,
            time: time2Date(time / 1000, 'h:i:s'),
            amount,
            side: +side,
          }
        }).reverse()
      }
    },
    async queryAccountOpend() {
      const { success, data } = await getAccountOpend()
      if (success)
        this.accountOpend = data
    },
    async getHotSymbols() {
      const { success, data } = await getContractsHotSymbolsApi()
      if (success) {
        this.hotSymbols = data || []
      }
    },
    _localFormatter() {
      return new Intl.NumberFormat('en-us', { maximumSignificantDigits: 20 })?.format
    },
    toLocal() { // 千分符 123,456.78
      return (n) => {
        if (Number.isNaN(n) || !this._localFormatter || +n === 0)
          return n

        return this._localFormatter(n)
      }
    },
    toHuman(n, precision) { // KMB缩写 1.23M
      return Number.isNaN(+n) ? n : n < 1e6 ? fix(n, precision) : (+n).toLocaleString(this.lang, { notation: 'compact', compactDisplay: 'short', maximumFractionDigits: 3, roundingPriority: 'lessPrecision', useGrouping: false })
    },
  },
})
